import { Box, Button } from '@factorialco/deprecated-design-system'
import { UnassignLocationDocument, UpdateEmployeeDocument } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  to: string
  employeeId: number
  accessId: number
  isLocationDefault: boolean
  locationId: number
  canAssignLocations: boolean
  onDefaultLocationUpdated: (id: number) => void
}

const WorkplacesActions = ({
  to,
  employeeId,
  accessId,
  isLocationDefault,
  locationId,
  canAssignLocations,
  onDefaultLocationUpdated,
}: Props) => {
  const [unassignLocation] = useMutation(UnassignLocationDocument, {
    extractorFn: (data) => data.locationsMutations.unassignLocationsEmployeesLocation,
    update: (cache) => {
      if (employeeId && locationId) {
        // Remove the location from the employee's locations array in the cache
        cache.modify({
          id: cache.identify({ __typename: 'EmployeesEmployee', id: employeeId }),
          fields: {
            locations(existingLocations = []) {
              // Filter out the location being unassigned
              return existingLocations.filter((locationEntry: { location: { __ref: string } }) => {
                const locationRef = locationEntry?.location?.__ref
                if (!locationRef) return true
                // Extract the ID from the cache reference (format: "LocationsLocation:ID")
                const locationIdFromRef = locationRef.split(':')[1]
                return locationIdFromRef !== String(locationId)
              })
            },
          },
        })
      }
    },
  })

  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
  })

  if (!canAssignLocations) {
    return null
  }

  const removeFromLocation = async () => {
    const result = await unassignLocation({
      variables: {
        employeeId: employeeId,
        locationId: locationId,
      },
    })

    const handledResult = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.workplaces.remove_from_workplace_error'),
        })
      }
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.workplaces.remove_from_workplace'),
      })
    }
  }

  const setAsDefaultLocation = async () => {
    const result = await updateEmployee({
      variables: {
        id: employeeId,
        accessId: accessId,
        locationId: locationId,
      },
    })

    const handled = handleResult(result, (_errors) => {
      return
    })

    if (handled.success) onDefaultLocationUpdated(locationId)
  }

  return (
    <Box flexDirection="row" gap="s12">
      {!isLocationDefault && (
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center">
              <PopoverMenu.TouchableOption
                title={i18n.t('employee_form.workplaces.set_default')}
                onClick={setAsDefaultLocation}
              />
              <PopoverMenu.TouchableOption
                title={i18n.t('employee_form.workplaces.remove_from_workplace')}
                onClick={removeFromLocation}
                danger
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}
      <Button.Router to={to} type="secondary" iconName="arrow-right" />
    </Box>
  )
}

export default WorkplacesActions
